import { fetchJSON } from '@bonnet/next/fetch';

import { brands } from '@atc/brand';
import { cmApiUrl } from '@atc/cm-api-url';

import srpReuseActiveStateDuck from '@/ducks/srp/srpReuseActiveStateDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';

import addNewCarBoostData from './addNewCarBoostData';

export function assignRequiredSearchParams({
    defaultNumRecordsOptions = {},
    enableStrongAttributesFilters = false,
    enableSuggestedFilters = false,
    isFordBrand = false,
    lscSearchRequired = {},
    query = {},
    strongAttributesPixallId = '',
}) {

    if (enableStrongAttributesFilters) {
        Object.assign(lscSearchRequired, {
            showStrongAttributes: true,
        });
    }

    if (enableStrongAttributesFilters && strongAttributesPixallId) {
        Object.assign(lscSearchRequired, {
            pixallId: strongAttributesPixallId,
        });
    }

    if (enableSuggestedFilters) {
        Object.assign(lscSearchRequired, {
            includeSuggested: true,
        });
    }

    if (!!isFordBrand && !query?.numRecords) {
        Object.assign(lscSearchRequired, {
            numRecords: defaultNumRecordsOptions?.value,
        });
    }

    // fetch an appropriate number of records to get a number of listing cards that could be divisible by 3 after
    // special listings or their respective fallbacks are inserted - a number divisible by 3 is to avoid blank spaces
    // on larger viewports
    const defaultNumRecords = 30;
    const numRecordsValue = lscSearchRequired.numRecords || query?.numRecords || defaultNumRecords;
    const numRecordsToFetch = Number(numRecordsValue);

    Object.assign(lscSearchRequired, {
        numRecords: numRecordsToFetch,
    });
}

export default function addSearchResultsPageData() {

    return async (ctx) => {
        const {
            // feature configs
            default_num_records: [, { num_records: defaultNumRecordsOptions }],
            enable_new_car_boost: [enableNewCarBoost],
            default_query_params: [enableDefaultQueryParams, { queryParams }],
            non_relevant_sort_spotlights: [, {
                hide_spotlights: hideSpotlights,
            }],
            premium_spotlight: [enablePremiumSpotlight],
            spotlight: [isSpotlightEnabled],
            srp_inline_filters: [, {
                pixall_id: strongAttributesPixallId,
                strong_attributes_filters: enableStrongAttributesFilters,
            }],
            suggested_filters: [enableSuggestedFilters],
            vehicle_highlight: [isVehicleHighlightEnabled],
        } = ctx.useFeatures([
            'default_num_records',
            'enable_new_car_boost',
            'default_query_params',
            'non_relevant_sort_spotlights',
            'premium_spotlight',
            'spotlight',
            'srp_inline_filters',
            'suggested_filters',
            'vehicle_highlight',
        ]);

        const state = ctx.store.getState();
        const reuseActiveState = srpReuseActiveStateDuck.selectors.getDuckState(state);
        const sort = srpSortDuck.selectors.getDuckState(state);

        const isFordBrand = ctx.data?.brand === brands.FORD_BRAND;

        // listings endpoint //
        const listingsEndpoint = cmApiUrl('/rest/lsc/listing');
        const lscSearchRequired = ctx.data.requestOptions.requiredRequestLSC.query;
        const lscSearchOptional = ctx.data.requestOptions.optionalRequestLSC.query;

        assignRequiredSearchParams({
            defaultNumRecordsOptions,
            enableStrongAttributesFilters,
            enableSuggestedFilters,
            isFordBrand,
            lscSearchRequired,
            query: ctx.query,
            strongAttributesPixallId,
        });

        /*
          Add default experience query parameters to the required and optional searches
          See experience config files for more details:
           - src/config/experience/commercial.js
           - and src/config/experience/best-deals.js
        */

        if (enableDefaultQueryParams) {
            Object.assign(lscSearchRequired, { ...queryParams });
            Object.assign(lscSearchOptional, { ...queryParams });
        }

        const listingsRequestOptions = {
            ...ctx.data.requestOptions.requiredRequestLSC,
            query: lscSearchRequired,
            circuitBreaker: {
                ...ctx.data.requestOptions.requiredRequestLSC.circuitBreaker,
                id: 'lsc_listing',
            },
        };

        if (typeof window === 'undefined') {
            listingsRequestOptions.responseInterceptor = (response, { json, cookies }) => {
                json._cookies = cookies;
            };
        }

        const lscRequestOptional = {
            ...ctx.data.requestOptions.optionalRequestLSC,
            query: { ...lscSearchOptional, numSpotlights: 3 },
        };

        ctx.fetchCompositeJSON.addFetch('base', fetchJSON(listingsEndpoint, listingsRequestOptions));

        // spotlights endpoint //
        const getSpotlightsEndpoint = (resultType) => cmApiUrl(`/rest/lsc/listing/spotlight/${resultType}`);
        const getSpotlightRequestOptions = (listingTier, numSpotlights) => ({
            ...lscRequestOptional,
            query: {
                ...lscRequestOptional.query,
                numSpotlights,
                ...(listingTier !== 'vehicle_highlight' && { listingTier }),
                ...(listingTier === 'vehicle_highlight' && { includeExtendedVehicleHighlights: true }),
            },
            circuitBreaker: {
                ...lscRequestOptional.circuitBreaker,
                id: `spotlight_${listingTier}`,
            },
        });

        if (isSpotlightEnabled && !reuseActiveState) {
            if (isVehicleHighlightEnabled && enablePremiumSpotlight) {
                ctx.fetchCompositeJSON.addFetch('vehicleHighlight', fetchJSON(getSpotlightsEndpoint('vehiclehighlight'), getSpotlightRequestOptions('vehicle_highlight', 10)));
            }
            if (!hideSpotlights || (hideSpotlights && sort === 'relevance')) {
                ctx.fetchCompositeJSON.addFetch('spotlights', fetchJSON(getSpotlightsEndpoint('premiumresults'), getSpotlightRequestOptions('premium', 3)));
                ctx.fetchCompositeJSON.addFetch('featuredSpotlights', fetchJSON(getSpotlightsEndpoint('results'), getSpotlightRequestOptions('featured', 6)));
                ctx.fetchCompositeJSON.addFetch('standardSpotlights', fetchJSON(getSpotlightsEndpoint('results'), getSpotlightRequestOptions('standard', 6)));
            }
        }

        const shouldFetchNewCarBoost = enableNewCarBoost && !reuseActiveState;

        addNewCarBoostData(ctx, lscRequestOptional, shouldFetchNewCarBoost);
    };
}
